import { useDeferredValue, useMemo, useState } from 'react';

import {
  AnyTreeNode,
  TreeCheckState,
  TreeController,
  UseTreeOptions,
  VisibleTreeNode,
} from './types';
import {
  collectFolderIds,
  filterTree,
  findNodeById,
  flattenVisibleNodes,
  toggleInSet,
  walkTree,
} from './utils';

type CheckNodeState = { state: TreeCheckState; disabled: boolean };
type NodeIndexEntry<TFolder, TLeaf> = {
  node: AnyTreeNode<TFolder, TLeaf>;
  depth: number;
};

export const useTree = <TFolder = unknown, TLeaf = unknown>(
  options: UseTreeOptions<TFolder, TLeaf>,
): TreeController<TFolder, TLeaf> => {
  const {
    nodes,
    initialExpanded,
    defaultExpandedDepth,
    selectionMode = 'single',
    predicate,
    getSearchText,
  } = options;

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [checkedIds, setCheckedIds] = useState<Set<string>>(new Set());
  const [query, setQuery] = useState<string>('');

  const [expandedIds, setExpandedIds] = useState<Set<string>>(() => {
    if (initialExpanded) {
      return new Set(initialExpanded);
    }
    if (typeof defaultExpandedDepth === 'number') {
      return collectFolderIds(nodes, defaultExpandedDepth);
    }
    return new Set();
  });

  const nodeIndex = useMemo(() => {
    const map = new Map<string, NodeIndexEntry<TFolder, TLeaf>>();
    walkTree(nodes, (node, depth) => {
      map.set(node.id, { node, depth });
    });
    return map;
  }, [nodes]);

  // The search input stays responsive: filtering re-renders the whole tree, so
  // it runs against the deferred query in an interruptible background render.
  const deferredQuery = useDeferredValue(query);

  const { filteredNodes, forceExpand } = useMemo(() => {
    const result = filterTree(nodes, deferredQuery, predicate, getSearchText);
    return { filteredNodes: result.nodes, forceExpand: result.forceExpand };
  }, [nodes, deferredQuery, predicate, getSearchText]);

  const effectiveExpanded = useMemo<ReadonlySet<string>>(() => {
    if (forceExpand.size === 0) {
      return expandedIds;
    }
    const combined = new Set(expandedIds);
    forceExpand.forEach((id) => combined.add(id));
    return combined;
  }, [expandedIds, forceExpand]);

  const visibleNodes = useMemo<VisibleTreeNode<TFolder, TLeaf>[]>(
    () => flattenVisibleNodes(filteredNodes, effectiveExpanded),
    [filteredNodes, effectiveExpanded],
  );

  const getNode = (id: string) => nodeIndex.get(id)?.node ?? null;

  const getDepth = (id: string) => nodeIndex.get(id)?.depth ?? -1;

  const getAncestorIds = (id: string) => {
    const ids: string[] = [];
    let cursor = nodeIndex.get(id)?.node.parentId ?? null;
    while (cursor !== null) {
      ids.push(cursor);
      cursor = nodeIndex.get(cursor)?.node.parentId ?? null;
    }
    return ids.reverse();
  };

  const activeAncestorIds = useMemo<ReadonlySet<string>>(() => {
    if (!selectedId) {
      return new Set();
    }
    const ids = new Set<string>();
    let cursor = nodeIndex.get(selectedId)?.node.parentId ?? null;
    while (cursor !== null) {
      ids.add(cursor);
      cursor = nodeIndex.get(cursor)?.node.parentId ?? null;
    }
    return ids;
  }, [nodeIndex, selectedId]);

  const checkStateMap = useMemo<Map<string, CheckNodeState>>(() => {
    const map = new Map<string, CheckNodeState>();
    walkTree(filteredNodes, (node) => {
      if (node.kind === 'leaf') {
        map.set(node.id, {
          state: checkedIds.has(node.id) ? 'checked' : 'unchecked',
          disabled: false,
        });
        return;
      }
      const directLeaves = node.children.filter(
        (child) => child.kind === 'leaf',
      );
      if (directLeaves.length === 0) {
        map.set(node.id, { state: 'unchecked', disabled: true });
        return;
      }
      const checkedCount = directLeaves.filter((leaf) =>
        checkedIds.has(leaf.id),
      ).length;
      let state: TreeCheckState = 'indeterminate';
      if (checkedCount === 0) {
        state = 'unchecked';
      } else if (checkedCount === directLeaves.length) {
        state = 'checked';
      }
      map.set(node.id, { state, disabled: false });
    });
    return map;
  }, [filteredNodes, checkedIds]);

  const getCheckState = (id: string): TreeCheckState =>
    checkStateMap.get(id)?.state ?? 'unchecked';

  const isCheckDisabled = (id: string): boolean =>
    checkStateMap.get(id)?.disabled ?? false;

  const toggleExpanded = (id: string) => {
    setExpandedIds((prev) => toggleInSet(prev, id));
  };

  const expandMany = (ids: string[]) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      ids.forEach((id) => next.add(id));
      return next;
    });
  };

  const expandAll = () => {
    setExpandedIds(collectFolderIds(nodes));
  };

  const collapseAll = () => {
    setExpandedIds(new Set());
  };

  const isExpanded = (id: string) => effectiveExpanded.has(id);

  const selectNode = (id: string | null) => {
    if (selectionMode === 'none') {
      return;
    }
    setSelectedId(id);
  };

  const toggleChecked = (id: string) => {
    const node = findNodeById(filteredNodes, id);
    if (!node) {
      return;
    }
    if (node.kind === 'leaf') {
      setCheckedIds((prev) => toggleInSet(prev, id));
      return;
    }
    const directLeafIds = node.children
      .filter((child) => child.kind === 'leaf')
      .map((child) => child.id);
    if (directLeafIds.length === 0) {
      return;
    }
    setCheckedIds((prev) => {
      const allChecked = directLeafIds.every((leafId) => prev.has(leafId));
      const next = new Set(prev);
      directLeafIds.forEach((leafId) => {
        if (allChecked) {
          next.delete(leafId);
        } else {
          next.add(leafId);
        }
      });
      return next;
    });
  };

  const checkMany = (ids: string[]) => {
    setCheckedIds((prev) => {
      const next = new Set(prev);
      ids.forEach((id) => next.add(id));
      return next;
    });
  };

  const clearChecked = () => {
    setCheckedIds(new Set());
  };

  return {
    nodes,
    visibleNodes,
    expandedIds: effectiveExpanded,
    selectedId,
    checkedIds,
    query,
    activeAncestorIds,
    selectionMode,
    toggleExpanded,
    expandMany,
    expandAll,
    collapseAll,
    isExpanded,
    selectNode,
    toggleChecked,
    checkMany,
    clearChecked,
    getCheckState,
    isCheckDisabled,
    setQuery,
    getNode,
    getDepth,
    getAncestorIds,
  };
};
